import { useCallback, useEffect, useRef } from 'react';
import { Navigate, Outlet, useSearchParams } from 'react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { KycCurrentStatus, KycProvider, kycQueries } from '@repo/api-kit/queries/due-diligence/kyc';
import { CFT } from '@repo/shared-kit/sentry/sentry-tags';
import { useSentry } from '@repo/shared-kit/sentry/context';
import { isPersonalInfoProvided } from '#utils/is-personal-info-provided';
import { shouldSubmitKyc } from '#utils/should-submit-kyc';
import { isKycSubmitted } from '#utils/is-kyc-submitted';
import { useKycContext } from '../context/kyc-context';
import { useKycTracking } from '#tracking/use-kyc-tracking';
import { useKycFlowSpan } from '#tracking/use-kyc-flow-span';

/**
 * Guard for KYC routes.
 * Checks if KYC should be submitted (required but not yet submitted).
 * If KYC should be submitted, renders child routes.
 * If KYC should not be submitted (already accepted or submitted), redirects via onComplete callback.
 * This guard is the same for all KYC routes - the redirect logic is handled by onComplete.
 *
 * @example
 * ```tsx
 * // In app router:
 * <Route element={<KycGuard onComplete={handleComplete} />}>
 *   <Route path="poi" element={<OwnerPoiPage />} />
 * </Route>
 * ```
 */
export function KycAccessGuard() {
  const { onComplete, isRegisterFlow, legalCountry } = useKycContext();
  const events = useKycTracking();
  const sentry = useSentry();
  const market = isRegisterFlow ? legalCountry : undefined;
  const { data: kycInfo } = useSuspenseQuery(kycQueries.item({ market }));
  const cancelled = useRef(false);
  const [searchParams] = useSearchParams();
  const { markError } = useKycFlowSpan();

  const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const submitKyc = shouldSubmitKyc(kycInfo);
  const hasUnexpectedPayload =
    !submitKyc && kycInfo.kycCurrentStatus === KycCurrentStatus.pending && !kycInfo.activeProcess;

  const trackSkipKyc = useCallback(() => {
    events.kycSkipped();
  }, [events]);

  const kycSubmitted = isKycSubmitted(kycInfo);

  useEffect(() => {
    if (submitKyc || cancelled.current || hasUnexpectedPayload || kycSubmitted) return;

    trackSkipKyc();

    onComplete(KycProvider.PROVIDER_UNSPECIFIED, 'skipped');
  }, [submitKyc, onComplete, trackSkipKyc, kycSubmitted, hasUnexpectedPayload]);

  useEffect(() => {
    return () => {
      cancelled.current = true;
    };
  }, []);

  if (hasUnexpectedPayload) {
    const error = new Error(
      'KYC process expected but not found: kycCurrentStatus is pending with no activeProcess'
    );
    markError(error);
    sentry.captureException(error, {
      tags: { component: 'KycAccessGuard' },
      cft: CFT.IDENTITY_VERIFICATION,
    });
    throw error;
  }

  if (kycSubmitted) {
    return <Navigate relative="path" replace to={`../waiting-results${search}`} />;
  }

  if (!submitKyc) {
    return null;
  }

  if (!isPersonalInfoProvided(kycInfo)) {
    return <Navigate relative="path" replace to={`../user-info${search}`} />;
  }

  return <Outlet />;
}
